import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MtxSelectModule } from '@ng-matero/extensions/select';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { TranslateModule } from '@ngx-translate/core';
import { CommonMaterialModule } from '@nuso/common/ui-material';
import { CommonUiComponentsModule } from '@nuso/common/ui-components';

import { FormControlComponent } from './form-control/form-control.component';
import { TextInputComponent } from './inputs/text-input/text-input.component';
import { TextareaInputComponent } from './inputs/textarea-input/textarea-input.component';
import { SelectInputComponent } from './inputs/select-input/select-input.component';
import { CheckboxInputComponent } from './inputs/checkbox-input/checkbox-input.component';
import { CheckSelectInputComponent } from './inputs/check-select-input/check-select-input.component';
import { GroupInputComponent } from './inputs/group-input/group-input.component';
import { DividerInputComponent } from './inputs/divider-input/divider-input.component';
import { StringListInputComponent } from './inputs/string-list-input/string-list-input.component';
import { ReadonlyInputComponent } from './inputs/readonly-input/readonly-input.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { WysiwygInputComponent } from './inputs/wysiwyg-input/wysiwyg-input.component';
import { ReadonlyTextareaInputComponent } from './inputs/readonly-textarea-input/readonly-textarea-input.component';
import { ForminatorErrorService } from './forminator-error-service';
import { RadioInputComponent } from './inputs/radio-input/radio-input.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DatePickerInputComponent } from './inputs/date-picker-input/date-picker-input.component';
import { AutocompleteInputComponent } from './inputs/autocomplete-input/autocomplete-input.component';

import { FormDialogComponent } from './form-dialog/form-dialog.component';
import { FormDialogService } from './form-dialog/form-dialog.service';
import { FormViewComponent } from './form-view/form-view.component';
import { ToggleCheckboxDirective } from './toggle-checkbox.directive';
import { CommonPipesModule } from '@nuso/common/util-pipes';
import { FormSectionComponent } from './form-section/form-section.component';
import { SearchSelectInputComponent } from './inputs/search-select-input/search-select-input.component';
import { SliderInputComponent } from './inputs/slider-input/slider-input.component';
import { MatSliderModule } from '@angular/material/slider';
import { RowInputComponent } from './inputs/row-input/row-input.component';
import { IconButtonInputComponent } from './inputs/icon-button-input/icon-button-input.component';
import { SlideToggleComponent } from './inputs/slide-toggle-input/slide-toggle.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MultipleSearchSelectInputComponent } from './inputs/multiple-search-select-input/multiple-search-select-input.component';
import { MultipleSearchSelectPlusInputComponent } from './inputs/multiple-search-select-plus-input/multiple-search-select-plus-input.component';
import { EditablePropertiesCardComponent } from './editable-properties-card/editable-properties-card.component';
import { SelectOptionsPipe } from './select-options.pipe';
import { EmptySelectOptionPipe } from './empty-select-option.pipe';
import { ChangePasswordDialogComponent } from './change-password-dialog/change-password-dialog.component';
import { DateRangeInputComponent } from './inputs/date-range-input/date-range-input.component';
import { TimezoneSelectComponent } from './timezone-select/timezone-select.component';
import { TINYMCE_SCRIPT_URL } from './tinymce/tinymce-init';

export { ChangePasswordDialogComponent };

@NgModule({
  imports: [
    CommonModule,
    CommonPipesModule,
    CommonUiComponentsModule,
    TranslateModule,
    MtxSelectModule,
    FormsModule,
    ReactiveFormsModule,
    EditorModule,
    CommonMaterialModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatRadioModule,
    MatSelectModule,
    MatSliderModule,
    MatSlideToggleModule,
  ],
  declarations: [
    FormControlComponent,
    TextInputComponent,
    TextareaInputComponent,
    SelectInputComponent,
    CheckboxInputComponent,
    CheckSelectInputComponent,
    GroupInputComponent,
    ReadonlyInputComponent,
    StringListInputComponent,
    WysiwygInputComponent,
    ReadonlyTextareaInputComponent,
    RadioInputComponent,
    DatePickerInputComponent,
    AutocompleteInputComponent,
    ToggleCheckboxDirective,
    FormDialogComponent,
    FormViewComponent,
    FormSectionComponent,
    ChangePasswordDialogComponent,
    SearchSelectInputComponent,
    MultipleSearchSelectInputComponent,
    MultipleSearchSelectPlusInputComponent,
    SliderInputComponent,
    RowInputComponent,
    DividerInputComponent,
    IconButtonInputComponent,
    SlideToggleComponent,
    EditablePropertiesCardComponent,
    EmptySelectOptionPipe,
    SelectOptionsPipe,
    DateRangeInputComponent,
    TimezoneSelectComponent,
  ],
  exports: [
    FormControlComponent,
    TextInputComponent,
    TextareaInputComponent,
    SelectInputComponent,
    CheckboxInputComponent,
    CheckSelectInputComponent,
    GroupInputComponent,
    StringListInputComponent,
    ReadonlyTextareaInputComponent,
    RadioInputComponent,
    DatePickerInputComponent,
    AutocompleteInputComponent,
    ToggleCheckboxDirective,
    FormViewComponent,
    FormSectionComponent,
    EditablePropertiesCardComponent,
    EmptySelectOptionPipe,
    SelectOptionsPipe,
    ChangePasswordDialogComponent,
    TimezoneSelectComponent,
  ],
  providers: [
    ForminatorErrorService,
    FormDialogService,
    { provide: TINYMCE_SCRIPT_SRC, useValue: TINYMCE_SCRIPT_URL },
  ],
})
export class FormsUiModule {}
